import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { X, Home } from 'lucide-react';
import { useContent } from '../hooks/useContent';
import { RichText } from './RichText';
import { PeopleGrid } from './PeopleGrid';
import { StaffTeamGrid } from './StaffTeamGrid';

const defaultData = {
  foundation_title: 'OUR FOUNDATION IS ALL ABOUT CHANGING LIVES',
  foundation_text:
    'The Educational, Welfare and Research Foundation (EWRF) is a Malaysian NPO & NGO, founded in 1979, whose mission is to empower marginalised communities in Malaysia by creating platforms for empowerment through education, psycho-social counseling and welfare for B40 communities.',
  foundation_objectives: [
    'Community development by empowering academically under performing and socially challenged students',
    'Increasing and sharing innovative approaches for information exchange through experimentation, research and technology',
    'Developing innovative platforms related to educational, social, counseling and welfare',
  ],
  core_programmes: [
    'Language for Juniors (L4J)',
    'Civil Society Intervention (CSI)',
    'Skill Training Initiative (STI)',
    'Volunteer Induction Programme (VIP)',
    'Counseling',
    'Research',
    'Medical Aid and Welfare',
  ],
  history_title: 'EWRF 46 year journey since 1979',
  history_text:
    'Here you will be able to trace back what started as a support for a few promising children by well intentioned professionals, over the years has become an esteemed organisation of dedicated citizens who care for those who may have been left behind in the midst of a faced paced growing nation.',
  history_images: [],
  committee_title: 'The People Behind The Foundation',
  committee_subtitle: 'Currently serving Central Committee Members (2024-2026)',
  committee_text: 'As per EWRF constitution, an election is conducted every two years to elect committee members.',
  committee_images: [],
  past_presidents_title: 'Past Presidents',
  past_presidents_text: 'Honoring the leaders who have guided EWRF through the years',
  past_presidents_images: [],
  staff_title: 'Our Team',
  staff_text: 'Meet the dedicated staff members who make our programmes possible',
  staff_images: [],
};

const Section = ({ title, text, images }) => {
  if (!title && !text && (!images || images.length === 0)) return null;
  return (
    <section className="mb-12">
      <div className="text-center mb-6">
        {title && <h2 className="text-3xl md:text-4xl font-serif text-[#D4AF37] mb-3">{title}</h2>}
        {text && <RichText html={text} className="text-gray-600 max-w-4xl mx-auto" plainClass="text-gray-600 max-w-4xl mx-auto" />}
      </div>
      <div className={`grid gap-4 ${images && images.length > 1 ? 'md:grid-cols-2' : ''}`}>
        {images?.map((url, idx) => (
          <div key={idx} className="aspect-[16/10] w-full rounded-lg shadow-lg overflow-hidden bg-gray-100">
            <img src={url} className="w-full h-full object-cover" alt="" />
          </div>
        ))}
      </div>
    </section>
  );
};

const SectionDivider = ({ label }) => (
  <div className="flex items-center gap-4 mb-6" data-testid={`divider-${(label || '').toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-[#D4AF37]/40"></div>
    <span className="px-3 py-1 rounded-full bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] md:text-xs font-black uppercase tracking-[0.25em] whitespace-nowrap">
      {label}
    </span>
    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-[#D4AF37]/40"></div>
  </div>
);

const AboutModal = ({ isOpen, onClose }) => {
  const { data } = useContent('about-modal', defaultData);
  const d = { ...defaultData, ...(data || {}) };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] p-0 overflow-hidden border-0 shadow-2xl">
        <DialogHeader className="sticky top-0 z-10 bg-gradient-to-r from-white via-white to-amber-50/40 border-b border-amber-100 p-6 shadow-sm">
          {/* Decorative gold accent bar */}
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"></div>
          <DialogTitle className="text-3xl font-serif text-[#0A1628] flex items-center gap-2">
            About <span className="italic text-[#D4AF37]">EWRF</span>
            <span className="ml-1 text-[10px] font-sans font-black uppercase tracking-[0.3em] text-gray-400">Est. 1979</span>
          </DialogTitle>
          <button
            onClick={onClose}
            className="absolute right-4 top-4 rounded-full p-2 hover:bg-gray-100 transition-colors"
            data-testid="about-modal-close"
          >
            <X className="w-5 h-5" />
          </button>
        </DialogHeader>

        <div className="h-[calc(90vh-100px)] px-6 pb-8 overflow-y-auto overflow-x-hidden">
          {/* ── 1. STATS STRIP ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mt-4 mb-10">
            {[
              { value: '47+', label: 'Years of Impact', color: 'from-amber-400 to-orange-500' },
              { value: '15K+', label: 'Lives Changed',  color: 'from-rose-400 to-pink-500' },
              { value: '500+', label: 'Volunteers',     color: 'from-emerald-400 to-teal-500' },
              { value: '120+', label: 'Partner Schools', color: 'from-violet-400 to-fuchsia-500' },
            ].map((s) => (
              <div
                key={s.label}
                className="group relative bg-white rounded-2xl p-4 text-center shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
              >
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${s.color}`}></div>
                <div className={`absolute -bottom-6 -right-6 w-20 h-20 rounded-full bg-gradient-to-br ${s.color} opacity-10 group-hover:opacity-20 transition-opacity blur-xl`}></div>
                <div className={`text-3xl md:text-4xl font-black bg-gradient-to-r ${s.color} bg-clip-text text-transparent`}>
                  {s.value}
                </div>
                <div className="text-[10px] md:text-[11px] uppercase tracking-widest font-bold text-gray-500 mt-1.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* ── 2. FOUNDATION ── */}
          <SectionDivider label="Our Foundation" />
          <section className="mb-16 relative bg-gradient-to-br from-[#0A1628] via-[#0F2440] to-[#1A2F4A] rounded-3xl p-8 md:p-10 text-white shadow-2xl overflow-hidden">
            {/* Decorative blurs */}
            <div className="absolute -top-20 -right-20 w-80 h-80 bg-amber-400/15 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-32 -left-20 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>

            <div className="relative">
              <h2 className="text-2xl md:text-3xl font-bold mb-6 border-l-4 border-[#D4AF37] pl-4">
                {d.foundation_title}
              </h2>
              {d.foundation_text && (
                <RichText html={d.foundation_text} className="text-base md:text-lg leading-relaxed mb-6 text-white/95" plainClass="text-base md:text-lg leading-relaxed mb-6 whitespace-pre-line text-white/95" />
              )}
              {d.foundation_objectives?.length > 0 && (
                <ul className="space-y-3 mb-8 ml-1">
                  {d.foundation_objectives.map((obj, i) => (
                    <li key={i} className="flex items-start gap-3 group">
                      <span className="flex-shrink-0 w-7 h-7 rounded-md bg-gradient-to-br from-amber-400 to-[#D4AF37] text-[#0A1628] text-xs font-black flex items-center justify-center mt-0.5 shadow-md group-hover:scale-110 transition-transform">{i + 1}</span>
                      <span className="text-white/95 leading-relaxed">{obj}</span>
                    </li>
                  ))}
                </ul>
              )}
              {d.core_programmes?.length > 0 && (
                <div className="bg-white/10 rounded-2xl p-6 backdrop-blur-md border border-white/10">
                  <h3 className="text-xl font-semibold mb-4 text-[#D4AF37] flex items-center gap-2">
                    <span className="inline-block w-8 h-px bg-[#D4AF37]"></span>
                    EWRF Core Programmes
                  </h3>
                  <div className="grid md:grid-cols-2 gap-x-6 gap-y-3">
                    {d.core_programmes.map((p, i) => (
                      <div key={i} className="flex items-center gap-2.5 text-sm">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></span>
                        <span className="text-white/90">{p}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* ── 3. HISTORY ── */}
          <SectionDivider label={d.history_eyebrow || 'Our History'} />
          <section className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-serif text-[#D4AF37] mb-3">
                {d.history_title || "EWRF's journey since 1979"}
              </h2>
              {d.history_text ? (
                <div className="text-sm md:text-base text-gray-600 italic max-w-3xl mx-auto">
                  <RichText html={d.history_text} className="" plainClass="whitespace-pre-line" />
                </div>
              ) : (
                <p className="text-sm md:text-base text-gray-600 italic max-w-3xl mx-auto">
                  Here you will be able to trace back what started as a support for a few promising
                  children by well intentioned professionals, over the years has become an esteemed
                  organisation of dedicated citizens who care for those who may have been left behind
                  in the midst of a fast paced growing nation.
                </p>
              )}
            </div>

            {Array.isArray(d.history_phases) && d.history_phases.length > 0 ? (
              <div className="space-y-10">
                {d.history_phases.map((p, i) => {
                  const reversed = i % 2 === 1;
                  // Static class map so Tailwind's JIT can pick up all classes
                  const LABEL_POS = {
                    'top-left': 'top-3 left-3',
                    'top-right': 'top-3 right-3',
                    'bottom-left': 'bottom-3 left-3',
                    'bottom-right': 'bottom-3 right-3',
                  };
                  const labelPos = LABEL_POS[p.label_pos] || LABEL_POS['top-left'];
                  // Per-phase image fit — 'cover' (fills tile, may crop) or 'contain' (fully visible)
                  const imgFit = p.image_fit === 'contain' ? 'object-contain' : 'object-cover';
                  const bgClass = p.image_fit === 'contain' ? 'bg-gradient-to-br from-[#f5f0e5] to-[#e6dcc8]' : 'bg-gray-100';
                  const Image = (
                    <div className={`relative overflow-hidden rounded-2xl shadow-xl ring-1 ring-gray-200 min-w-0 group ${bgClass}`}>
                      {p.image ? (
                        <>
                          {/* Responsive aspect ratio — same visual proportions on phone / tablet / laptop / desktop */}
                          <div className="w-full aspect-[16/10] sm:aspect-[16/10] md:aspect-[4/3] lg:aspect-[16/10]">
                            <img
                              src={p.image}
                              alt={p.title}
                              className={`w-full h-full ${imgFit} object-center group-hover:scale-[1.02] transition-transform duration-700`}
                            />
                          </div>
                          {p.image_fit !== 'contain' && (
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent h-1/3 pointer-events-none"></div>
                          )}
                          <div className={`absolute ${labelPos} bg-[#D4AF37] text-[#0A1628] text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded shadow-lg`}>
                            {p.title}
                          </div>
                        </>
                      ) : (
                        <div className="w-full aspect-[16/10] md:aspect-[4/3] lg:aspect-[16/10] bg-gradient-to-br from-[#0A1628] to-[#1A2F4A]" />
                      )}
                    </div>
                  );
                  const Text = (
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-black mb-2 flex items-center gap-2">
                        <span className="w-8 h-px bg-[#D4AF37]/60"></span>
                        {p.period}
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4">{p.title}</h3>
                      <div
                        className="text-sm md:text-base text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: p.text }}
                      />
                    </div>
                  );
                  return (
                    <div
                      key={p.id || p.title || i}
                      className="grid md:grid-cols-2 gap-6 md:gap-10 items-center w-full"
                      data-testid={`history-phase-${i}`}
                    >
                      {reversed ? Text : Image}
                      {reversed ? Image : Text}
                    </div>
                  );
                })}
              </div>
            ) : (
              d.history_text && (
                <div className="prose prose-sm md:prose-base max-w-none text-gray-700 prose-headings:text-[#0A1628] prose-headings:font-bold prose-strong:text-[#1565C0] mx-auto">
                  <RichText html={d.history_text} className="leading-relaxed" plainClass="leading-relaxed whitespace-pre-line" />
                </div>
              )
            )}
          </section>

          {/* ── 4. COMMITTEE ── */}
          <SectionDivider label="The People Behind The Foundation" />
          <section className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-serif text-[#D4AF37] mb-2">{d.committee_title}</h2>
              {d.committee_subtitle && (
                <p className="text-base md:text-lg text-gray-700 font-semibold">{d.committee_subtitle}</p>
              )}
              {d.committee_text && (
                <div className="text-sm text-gray-500 mt-2 max-w-2xl mx-auto">
                  <RichText html={d.committee_text} className="" plainClass="" />
                </div>
              )}
            </div>
            <PeopleGrid people={d.committee_members} variant="committee" />
          </section>

          {/* ── 5. PAST PRESIDENTS ── */}
          <SectionDivider label={d.past_presidents_eyebrow || 'Our Legacy'} />
          <section className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-serif text-[#D4AF37] mb-2">{d.past_presidents_title}</h2>
              <p className="text-sm md:text-base text-gray-600">{d.past_presidents_subtitle || d.past_presidents_text || 'Past Presidents who shaped EWRF (1979 – 2024)'}</p>
            </div>
            <PeopleGrid people={d.past_presidents_list} variant="past_presidents" />
          </section>

          {/* ── 6. STAFF ── */}
          <SectionDivider label={d.staff_eyebrow || 'Operational Team'} />
          <section className="mb-12 relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#0A1628] via-[#0D2444] to-[#1565C0] py-10 md:py-12 px-6 md:px-10 shadow-xl">
            {/* Decorative glows */}
            <div className="absolute -top-16 -right-10 w-64 h-64 bg-amber-400/15 rounded-full blur-3xl pointer-events-none" aria-hidden="true"></div>
            <div className="absolute -bottom-16 -left-10 w-72 h-72 bg-cyan-400/10 rounded-full blur-3xl pointer-events-none" aria-hidden="true"></div>
            <div className="relative">
              <div className="text-center mb-8">
                <h2 className="text-3xl md:text-4xl font-serif text-[#FFD700] mb-2 drop-shadow-lg">{d.staff_title}</h2>
                <p className="text-sm md:text-base text-white/85">{d.staff_subtitle || d.staff_text || 'The dedicated professionals running EWRF day-to-day'}</p>
              </div>
              <StaffTeamGrid people={d.staff_members} />
            </div>
          </section>

          {/* ── Back to Home CTA ── */}
          <div className="text-center pt-6 pb-4">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-[#1565C0] to-[#0F4C9E] text-white font-bold text-sm uppercase tracking-wider shadow-lg hover:shadow-2xl hover:scale-105 transition-all"
              data-testid="about-modal-back-home"
            >
              <Home className="w-4 h-4" />
              Back to Home
            </button>
            <p className="text-xs text-gray-500 mt-3">Thanks for reading. Return to the homepage to explore more.</p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AboutModal;
